import React, { useState, useCallback, useId } from "react";
import { ConfirmDialog } from "primereact/confirmdialog";
import NotificationPopUp from "util/NotificationPopUp";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { Tooltip } from "react-tooltip";
import "./JobRunnerToggleButton.scss";
import {JobInfoDTO} from "../../../serviceapi/jobRunnerApi";

interface JobRunnerProp {
  jobRunner: JobInfoDTO;
  onJobRunnerToggled(jobRunnerId?: string): void;
}

const JobRunnerToggleButton = ({
  jobRunner,
  onJobRunnerToggled,
}: JobRunnerProp) => {
  const id = useId();
  const [visible, setVisible] = useState<boolean>(false);
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);
  const [enabled, setEnabled] = useState<boolean | undefined>(
    jobRunner.enabled
  );

  const handleInputSwitchChange = (e: InputSwitchChangeEvent) => {
    setPendingValue(e.value);
    setVisible(true);
  };

  const getJobRunnerStatusMessages = useCallback(() => {
    if (enabled) {
      return {
        notification: "Stopping Job Runner",
        tooltip: "Turn Job Runner off",
        confirmation: "Do you want to turn off the job runner?",
      };
    }
    return {
      notification: "Starting Job Runner",
      tooltip: "Turn Job Runner on",
      confirmation: "Do you want to turn on the job runner?",
    };
  }, [enabled]);

  const { notification, tooltip, confirmation } = getJobRunnerStatusMessages();

  const handleJobRunnerToggle = useCallback(async () => {
    try {
      setEnabled(pendingValue ?? jobRunner.enabled);
      NotificationPopUp.show(notification, "success");
      onJobRunnerToggled(jobRunner.taskId);
    } catch (error) {
      NotificationPopUp.show(
        "An error occurred while toggling the Job Runner.",
        "error"
      );
      console.error(error);
    } finally {
      setVisible(false);
      setPendingValue(null);
    }
  }, [jobRunner.taskId, notification, onJobRunnerToggled, pendingValue]);

  return (
    <div className="toggle-button-custom">
      <Tooltip id={id} place="left" style={{ fontSize: 24 }} />
      <ConfirmDialog
        visible={visible}
        header="Confirmation"
        onHide={() => {
          setVisible(false);
          setPendingValue(null);
        }}
        message={confirmation}
        icon="pi pi-exclamation-triangle"
        accept={handleJobRunnerToggle}
      />
      <InputSwitch
        checked={enabled ?? false}
        onChange={handleInputSwitchChange}
        data-tooltip-id={id}
        data-tooltip-content={tooltip}
        className="jobrunner-switch"
      />
    </div>
  );
};

export default React.memo(JobRunnerToggleButton);
